import React, { useState } from 'react';
import { 
  Container, 
  Paper, 
  TextField, 
  Button, 
  Box, 
  Typography,
  CircularProgress,
  MenuItem,
  Select,
  InputLabel,
  FormControl,
  Switch,
  FormControlLabel,
  IconButton,
  Menu
} from '@mui/material';
import SendIcon from '@mui/icons-material/Send';
import PersonIcon from '@mui/icons-material/Person';
import SmartToyIcon from '@mui/icons-material/SmartToy';
import DarkModeIcon from '@mui/icons-material/DarkMode';
import LightModeIcon from '@mui/icons-material/LightMode';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import WbSunnyIcon from '@mui/icons-material/WbSunny';
import CloudIcon from '@mui/icons-material/Cloud';
import AcUnitIcon from '@mui/icons-material/AcUnit';
import GrainIcon from '@mui/icons-material/Grain';
import BoltIcon from '@mui/icons-material/Bolt';
import EmojiEmotionsIcon from '@mui/icons-material/EmojiEmotions';
import NewspaperIcon from '@mui/icons-material/Newspaper';
import RestaurantMenuIcon from '@mui/icons-material/RestaurantMenu';
import LightbulbIcon from '@mui/icons-material/Lightbulb';
import VolumeUpIcon from '@mui/icons-material/VolumeUp';
import MicIcon from '@mui/icons-material/Mic';
import ImageIcon from '@mui/icons-material/Image';
import CloseIcon from '@mui/icons-material/Close';
import CameraAltIcon from '@mui/icons-material/CameraAlt';
import PhotoLibraryIcon from '@mui/icons-material/PhotoLibrary';

// OpenRouter API anahtarınızı buraya ekleyin
const API_KEY = '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';

// Hava durumu ile ilgili anahtar kelimeler
const weatherKeywords = [
  'hava durumu', 'hava nasıl', 'bugün hava', 'yarın hava', 'hava sıcaklığı', 'hava', 'yağmur', 'kar', 'güneşli', 'bulutlu'
];

const MODEL_OPTIONS = [
  { label: 'TahsinAI', value: 'mistralai/mistral-7b-instruct' },
  { label: 'TahsinAI(Gelişmiş)', value: 'mistralai/mixtral-8x7b-instruct' }
];

const QUICK_COMMANDS = [
  { label: 'Hava', icon: <WbSunnyIcon sx={{ fontSize: 18 }} />, text: 'Bugün hava nasıl?' },
  { label: 'Şaka', icon: <EmojiEmotionsIcon sx={{ fontSize: 18 }} />, text: 'Bana bir şaka yap!' },
  { label: 'Motivasyon', icon: <BoltIcon sx={{ fontSize: 18 }} />, text: 'Günün motivasyon sözü' },
  { label: 'Haber', icon: <NewspaperIcon sx={{ fontSize: 18 }} />, text: 'Haberleri özetle' },
  { label: 'Yemek', icon: <RestaurantMenuIcon sx={{ fontSize: 18 }} />, text: 'Bir yemek tarifi öner' },
  { label: 'Fikir', icon: <LightbulbIcon sx={{ fontSize: 18 }} />, text: 'Bana yaratıcı bir fikir ver' },
  { label: 'Tavsiye', icon: <LightbulbIcon sx={{ fontSize: 18 }} />, text: 'Hayat tavsiyesi ver' },
];

function isWeatherQuery(text) {
  return weatherKeywords.some(keyword => text.toLowerCase().includes(keyword));
}

function extractCity(text) {
  // Basit şehir adı çıkarımı (daha gelişmişi için NER gerekir)
  // Örnek: "İstanbul'da hava nasıl?" => "İstanbul"
  const cityMatch = text.match(/([a-zA-ZçğıöşüÇĞİÖŞÜ]+)(?:'da|de|ta|te)?\s+hava/iu);
  if (cityMatch) return cityMatch[1];
  return null;
}

// Hava durumu kartı için yardımcı fonksiyon
function WeatherCard({ data, city }) {
  if (!data || !data.weather || !data.main) return null;
  const iconCode = data.weather[0].icon;
  const iconUrl = `https://openweathermap.org/img/wn/${iconCode}@2x.png`;
  const desc = data.weather[0].description;
  const temp = Math.round(data.main.temp);
  const humidity = data.main.humidity;
  const wind = data.wind.speed;
  return (
    <Box className="weather-card">
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
        <img src={iconUrl} alt={desc} width={56} height={56} style={{ background: '#fff', borderRadius: 16 }} />
        <Box>
          <div className="weather-city">{city}</div>
          <div className="weather-desc">{desc.charAt(0).toUpperCase() + desc.slice(1)}</div>
        </Box>
        <div className="weather-temp">{temp}°C</div>
      </Box>
      <Box sx={{ display: 'flex', gap: 3, mt: 1, ml: 1 }}>
        <span className="weather-detail"><GrainIcon sx={{ fontSize: 18, mr: 0.5, mb: '-2px' }} /> {humidity}% nem</span>
        <span className="weather-detail"><AcUnitIcon sx={{ fontSize: 18, mr: 0.5, mb: '-2px' }} /> {wind} m/s rüzgar</span>
      </Box>
    </Box>
  );
}

// Image Search Component
function ImageSearch({ onImageSearch }) {
  const [selectedImage, setSelectedImage] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);

  const handleImageSelect = (event) => {
    const file = event.target.files[0];
    if (file && file.type.startsWith('image/')) {
      setSelectedImage(file);
      const reader = new FileReader();
      reader.onload = (e) => setPreviewUrl(e.target.result);
      reader.readAsDataURL(file);
    }
  };

  const handleClear = () => {
    setSelectedImage(null);
    setPreviewUrl(null);
  };

  return (
    <Box
      sx={{
        position: 'relative',
        width: '100%',
        mb: 2,
        borderRadius: 2,
        overflow: 'hidden',
        border: '1px solid',
        borderColor: 'divider',
        backgroundColor: 'background.paper',
      }}
    >
      {previewUrl ? (
        <Box sx={{ position: 'relative' }}>
          <img
            src={previewUrl}
            alt="Preview"
            style={{
              width: '100%',
              height: '150px',
              objectFit: 'cover',
            }}
          />
          <Box
            sx={{
              position: 'absolute',
              bottom: 0,
              left: 0,
              right: 0,
              display: 'flex',
              justifyContent: 'space-between',
              padding: 1,
              background: 'linear-gradient(to top, rgba(0,0,0,0.7), transparent)',
            }}
          >
            <IconButton
              onClick={handleClear}
              size="small"
              sx={{ color: 'white' }}
            >
              <CloseIcon />
            </IconButton>
            <Button
              variant="contained"
              size="small"
              onClick={() => onImageSearch(selectedImage)}
              sx={{ 
                backgroundColor: 'rgba(255,255,255,0.9)',
                color: 'text.primary',
                '&:hover': {
                  backgroundColor: 'rgba(255,255,255,1)',
                }
              }}
            >
              Ara
            </Button>
          </Box>
        </Box>
      ) : (
        <Box
          sx={{
            p: 2,
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            cursor: 'pointer',
          }}
          onClick={() => document.getElementById('imageInput').click()}
        >
          <ImageIcon sx={{ color: 'text.secondary' }} />
          <Typography variant="body2" color="text.secondary">
            Resim yüklemek için tıklayın
          </Typography>
          <input
            id="imageInput"
            type="file"
            accept="image/*"
            onChange={handleImageSelect}
            style={{ display: 'none' }}
          />
        </Box>
      )}
    </Box>
  );
}

function App() {
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [selectedModel, setSelectedModel] = useState(MODEL_OPTIONS[1].value);
  const [darkMode, setDarkMode] = useState(false);
  const [copiedIndex, setCopiedIndex] = useState(null);
  const messageListRef = React.useRef(null);
  const [speakingIndex, setSpeakingIndex] = useState(null);
  const [listening, setListening] = useState(false);
  const recognitionRef = React.useRef(null);
  const [showImageSearch, setShowImageSearch] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);
  const [cameraStream, setCameraStream] = useState(null);
  const [showCamera, setShowCamera] = useState(false);
  const videoRef = React.useRef(null);
  const canvasRef = React.useRef(null);

  React.useEffect(() => {
    document.body.classList.toggle('dark-mode', darkMode);
  }, [darkMode]);

  React.useEffect(() => {
    if (messageListRef.current) {
      messageListRef.current.scrollTop = messageListRef.current.scrollHeight;
    }
  }, [messages, loading]);

  // Modern karşılama mesajı (sadece ilk açılışta, kalıcı)
  React.useEffect(() => {
    if (messages.length === 0) {
      setMessages([
        {
          role: 'assistant',
          content: '👋 <b>Hoş geldin!</b> Ben <span style="color:#4f8cff;font-weight:600;">TahsinChatAI</span>. Sana yardımcı olabilmem için aşağıdan bir mesaj yazabilir veya kısa komutlardan birini seçebilirsin. <br/><br/> <span style="color:#7fbcff;">Hava durumu, şaka, motivasyon, haber, yemek tarifi ve daha fazlası için buradayım!</span>',
          isWelcome: true
        }
      ]);
    }
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!input.trim()) return;

    const userMessage = input.trim();
    setInput('');
    setMessages(prev => [...prev, { role: 'user', content: userMessage }]);
    setLoading(true);

    try {
      if (isWeatherQuery(userMessage)) {
        let city = extractCity(userMessage) || 'İstanbul';
        const weatherRes = await fetch(`https://tahsinchat-backend.onrender.com/api/weather?city=${encodeURIComponent(city)}`);
        const weatherData = await weatherRes.json();
        let weatherInfo;
        if (weatherData.weather && weatherData.main) {
          weatherInfo = `${city} için güncel hava durumu: ${weatherData.weather[0].description}, sıcaklık: ${weatherData.main.temp}°C, nem: ${weatherData.main.humidity}%, rüzgar: ${weatherData.wind.speed} m/s.`;
        } else {
          weatherInfo = `${city} için hava durumu alınamadı.`;
        }
        const response = await fetch('https://tahsinchat-backend.onrender.com/api/chat', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            model: selectedModel,
            messages: [
              ...messages,
              { role: 'user', content: `${userMessage}\n\nNot: ${weatherInfo}` }
            ]
          }),
        });
        const data = await response.json();
        if (!data.choices || !data.choices[0] || !data.choices[0].message) {
          setMessages(prev => [
            ...prev,
            { role: 'assistant', content: data.error?.message || 'API yanıtı alınamadı. Lütfen API anahtarınızı ve kotanızı kontrol edin.' }
          ]);
        } else {
          const assistantMessage = data.choices[0].message.content;
          setMessages(prev => [...prev, { role: 'assistant', content: assistantMessage }]);
        }
      } else {
        const response = await fetch('https://tahsinchat-backend.onrender.com/api/chat', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            model: selectedModel,
            messages: [...messages, { role: 'user', content: userMessage }]
          }),
        });
        const data = await response.json();
        if (!data.choices || !data.choices[0] || !data.choices[0].message) {
          setMessages(prev => [
            ...prev,
            { role: 'assistant', content: data.error?.message || 'API yanıtı alınamadı. Lütfen API anahtarınızı ve kotanızı kontrol edin.' }
          ]);
        } else {
          const assistantMessage = data.choices[0].message.content;
          setMessages(prev => [...prev, { role: 'assistant', content: assistantMessage }]);
        }
      }
    } catch (error) {
      console.error('Error:', error);
      setMessages(prev => [...prev, { 
        role: 'assistant', 
        content: 'Üzgünüm, bir hata oluştu. Lütfen tekrar deneyin.' 
      }]);
    } finally {
      setLoading(false);
    }
  };

  // Türkçe sesli okuma fonksiyonu (daha doğal ve akışkan)
  const speak = (text, index) => {
    if (!window.speechSynthesis) return;
    window.speechSynthesis.cancel();
    const utter = new window.SpeechSynthesisUtterance(text);
    // En doğal Türkçe sesi bulmaya çalış
    const voices = window.speechSynthesis.getVoices();
    utter.lang = 'tr-TR';
    utter.voice =
      voices.find(v => v.lang === 'tr-TR' && v.name.toLowerCase().includes('google')) ||
      voices.find(v => v.lang === 'tr-TR' && v.name.toLowerCase().includes('microsoft')) ||
      voices.find(v => v.lang.startsWith('tr')) ||
      null;
    utter.rate = 0.97;   // Biraz daha yavaş ve doğal
    utter.pitch = 1.05;  // Hafif doğal perde
    setSpeakingIndex(index);
    utter.onend = () => setSpeakingIndex(null);
    window.speechSynthesis.speak(utter);
  };

  // Mikrofonla konuşma başlat/durdur
  const handleMicClick = () => {
    if (!('webkitSpeechRecognition' in window) && !('SpeechRecognition' in window)) {
      alert('Tarayıcınız sesli giriş desteklemiyor.');
      return;
    }
    let SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!recognitionRef.current) {
      recognitionRef.current = new SpeechRecognition();
      recognitionRef.current.lang = 'tr-TR';
      recognitionRef.current.interimResults = false;
      recognitionRef.current.maxAlternatives = 1;
      recognitionRef.current.onresult = (event) => {
        const transcript = event.results[0][0].transcript;
        setInput(transcript);
        setListening(false);
        setTimeout(() => {
          document.getElementById('chat-input')?.focus();
        }, 50);
      };
      recognitionRef.current.onerror = () => setListening(false);
      recognitionRef.current.onend = () => setListening(false);
    }
    if (!listening) {
      setListening(true);
      recognitionRef.current.start();
    } else {
      setListening(false);
      recognitionRef.current.stop();
    }
  };

  const handleImageSearch = async (imageFile) => {
    setLoading(true);
    
    try {
      // Create a canvas to resize and compress the image
      const img = new Image();
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');
      
      // Create a promise to handle image loading
      const loadImage = () => {
        return new Promise((resolve, reject) => {
          img.onload = resolve;
          img.onerror = reject;
          img.src = URL.createObjectURL(imageFile);
        });
      };

      await loadImage();

      // Calculate new dimensions while maintaining aspect ratio
      const MAX_WIDTH = 800;
      const MAX_HEIGHT = 600;
      let width = img.width;
      let height = img.height;

      if (width > height) {
        if (width > MAX_WIDTH) {
          height = Math.round((height * MAX_WIDTH) / width);
          width = MAX_WIDTH;
        }
      } else {
        if (height > MAX_HEIGHT) {
          width = Math.round((width * MAX_HEIGHT) / height);
          height = MAX_HEIGHT;
        }
      }

      // Set canvas dimensions and draw resized image
      canvas.width = width;
      canvas.height = height;
      ctx.drawImage(img, 0, 0, width, height);

      // Convert to base64 with reduced quality
      const base64Image = canvas.toDataURL('image/jpeg', 0.7).split(',')[1];

      // Send the compressed image to the chat API
      const response = await fetch('https://tahsinchat-backend.onrender.com/api/chat', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          model: selectedModel,
          messages: [
            ...messages,
            { 
              role: 'user', 
              content: `Bu resmi analiz et ve içeriğini açıkla: ${base64Image}` 
            }
          ]
        }),
      });

      const data = await response.json();
      if (!data.choices || !data.choices[0] || !data.choices[0].message) {
        setMessages(prev => [
          ...prev,
          { 
            role: 'assistant', 
            content: 'Resim analizi sırasında bir hata oluştu. Lütfen tekrar deneyin.' 
          }
        ]);
      } else {
        const assistantMessage = data.choices[0].message.content;
        setMessages(prev => [...prev, { role: 'assistant', content: assistantMessage }]);
      }
    } catch (error) {
      console.error('Error:', error);
      setMessages(prev => [
        ...prev,
        { 
          role: 'assistant', 
          content: 'Resim analizi sırasında bir hata oluştu. Lütfen tekrar deneyin.' 
        }
      ]);
    } finally {
      setLoading(false);
    }
  };

  const handleImageButtonClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleCameraSelect = async () => {
    handleMenuClose();
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ 
        video: { 
          facingMode: 'environment',
          width: { ideal: 1280 },
          height: { ideal: 720 }
        } 
      });
      setCameraStream(stream);
      setShowCamera(true);
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
      }
    } catch (error) {
      console.error('Error accessing camera:', error);
      setMessages(prev => [...prev, { 
        role: 'assistant', 
        content: 'Kamera erişimi sağlanamadı. Lütfen kamera izinlerini kontrol edin.' 
      }]);
    }
  };

  const handleFileSelect = () => {
    handleMenuClose();
    document.getElementById('imageInput').click();
  };

  const handleCapture = () => {
    if (canvasRef.current && videoRef.current) {
      const context = canvasRef.current.getContext('2d');
      canvasRef.current.width = videoRef.current.videoWidth;
      canvasRef.current.height = videoRef.current.videoHeight;
      context.drawImage(videoRef.current, 0, 0);
      
      canvasRef.current.toBlob((blob) => {
        const file = new File([blob], 'camera-capture.jpg', { type: 'image/jpeg' });
        handleImageSearch(file);
        stopCamera();
      }, 'image/jpeg', 0.8);
    }
  };

  const stopCamera = () => {
    if (cameraStream) {
      cameraStream.getTracks().forEach(track => track.stop());
      setCameraStream(null);
    }
    setShowCamera(false);
  };

  // Add cleanup on unmount
  React.useEffect(() => {
    return () => {
      if (cameraStream) {
        cameraStream.getTracks().forEach(track => track.stop());
      }
    };
  }, [cameraStream]);

  return (
    <Container maxWidth="md" sx={{ height: '100vh', py: 4 }}>
      <Paper 
        elevation={3} 
        sx={{ 
          height: '100%', 
          display: 'flex', 
          flexDirection: 'column',
          bgcolor: '#fff'
        }}
      >
        <Box className="topbar">
          <span className="app-title modern-title">TahsinChatAI</span>
          <FormControl size="small" sx={{ minWidth: 220 }}>
            <InputLabel id="model-select-label">Model Seç</InputLabel>
            <Select
              labelId="model-select-label"
              id="model-select"
              value={selectedModel}
              label="Model Seç"
              onChange={e => setSelectedModel(e.target.value)}
            >
              {MODEL_OPTIONS.map(opt => (
                <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControlLabel
            control={
              <Switch
                checked={darkMode}
                onChange={() => setDarkMode(!darkMode)}
                color="primary"
              />
            }
            label={darkMode ? <DarkModeIcon sx={{ color: '#ffd700' }} /> : <LightModeIcon sx={{ color: '#4f8cff' }} />}
            sx={{ ml: 2 }}
          />
        </Box>

        <Box className="quick-commands" sx={{ display: 'flex', gap: 1, mb: 1, flexWrap: 'wrap' }}>
          {QUICK_COMMANDS.map((cmd, i) => (
            <Button
              key={i}
              variant="outlined"
              size="small"
              className="quick-chip"
              startIcon={cmd.icon}
              sx={{ borderRadius: 999, textTransform: 'none', fontWeight: 500, fontSize: '0.93rem', color: '#4f8cff', borderColor: '#e3e8f0', background: '#f8fafc', px: 1.2, minWidth: 0, boxShadow: '0 1px 4px rgba(79,140,255,0.04)', ':hover': { background: '#e3f2fd', borderColor: '#4f8cff' } }}
              onClick={() => {
                setInput(cmd.text);
                setTimeout(() => {
                  document.getElementById('chat-input')?.focus();
                }, 50);
              }}
            >
              {cmd.label}
            </Button>
          ))}
        </Box>

        <Box className="message-list" ref={messageListRef}>
          {messages.map((message, index) => {
            // Modern karşılama mesajı için özel gösterim
            if (message.isWelcome) {
              return (
                <Box
                  key={index}
                  className={`message-bubble assistant welcome-bubble`}
                  sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, position: 'relative', background: 'linear-gradient(135deg, #e0e7ef 0%, #f8fafc 100%)', boxShadow: '0 2px 12px rgba(79,140,255,0.07)' }}
                >
                  <span className="avatar-bubble assistant-avatar">
                    <SmartToyIcon sx={{ fontSize: 28 }} />
                  </span>
                  <Typography sx={{ flex: 1 }} component="div">
                    <span dangerouslySetInnerHTML={{ __html: message.content }} />
                  </Typography>
                  <button
                    className="speak-btn"
                    title="Sesli Oku"
                    onClick={() => speak(message.content.replace(/<[^>]+>/g, ''), index)}
                    style={{
                      background: 'none', border: 'none', cursor: 'pointer', marginLeft: 8, opacity: 0.7, display: 'flex', alignItems: 'center', outline: 'none'
                    }}
                  >
                    <VolumeUpIcon sx={{ fontSize: 22, color: speakingIndex === index ? '#4f8cff' : '#b0b8c4' }} />
                  </button>
                </Box>
              );
            }
            // Hava durumu kartı için özel gösterim
            if (message.role === 'assistant' && message.weatherData) {
              return (
                <Box
                  key={index}
                  className={`message-bubble ${message.role}`}
                  sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, position: 'relative', background: 'transparent', boxShadow: 'none' }}
                >
                  <span className="avatar-bubble assistant-avatar">
                    <SmartToyIcon sx={{ fontSize: 28 }} />
                  </span>
                  <WeatherCard data={message.weatherData} city={message.city} />
                  <button
                    className="copy-btn"
                    title="Kopyala"
                    onClick={async () => {
                      await navigator.clipboard.writeText(`${message.city} için hava durumu: ${message.weatherData.weather[0].description}, sıcaklık: ${message.weatherData.main.temp}°C, nem: ${message.weatherData.main.humidity}%, rüzgar: ${message.weatherData.wind.speed} m/s.`);
                      setCopiedIndex(index);
                      setTimeout(() => setCopiedIndex(null), 1200);
                    }}
                  >
                    <ContentCopyIcon sx={{ fontSize: 18, color: '#b0b8c4' }} />
                    {copiedIndex === index && (
                      <span style={{ fontSize: 12, color: '#4f8cff', marginLeft: 4 }}>Kopyalandı!</span>
                    )}
                  </button>
                  <button
                    className="speak-btn"
                    title="Sesli Oku"
                    onClick={() => speak(`${message.city} için hava durumu: ${message.weatherData.weather[0].description}, sıcaklık: ${message.weatherData.main.temp}°C, nem: ${message.weatherData.main.humidity}%, rüzgar: ${message.weatherData.wind.speed} m/s.`, index)}
                    style={{
                      background: 'none', border: 'none', cursor: 'pointer', marginLeft: 8, opacity: 0.7, display: 'flex', alignItems: 'center', outline: 'none'
                    }}
                  >
                    <VolumeUpIcon sx={{ fontSize: 22, color: speakingIndex === index ? '#4f8cff' : '#b0b8c4' }} />
                  </button>
                </Box>
              );
            }
            // Normal mesaj balonu
            return (
              <Box
                key={index}
                className={`message-bubble ${message.role}`}
                sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, position: 'relative' }}
              >
                {message.role === 'user' ? (
                  <span className="avatar-bubble user-avatar">
                    <PersonIcon sx={{ fontSize: 28 }} />
                  </span>
                ) : (
                  <span className="avatar-bubble assistant-avatar">
                    <SmartToyIcon sx={{ fontSize: 28 }} />
                  </span>
                )}
                <Typography sx={{ flex: 1 }}>{message.content}</Typography>
                {message.role === 'assistant' && (
                  <button
                    className="speak-btn"
                    title="Sesli Oku"
                    onClick={() => speak(message.content, index)}
                    style={{
                      background: 'none', border: 'none', cursor: 'pointer', marginLeft: 8, opacity: 0.7, display: 'flex', alignItems: 'center', outline: 'none'
                    }}
                  >
                    <VolumeUpIcon sx={{ fontSize: 22, color: speakingIndex === index ? '#4f8cff' : '#b0b8c4' }} />
                  </button>
                )}
                <button
                  className="copy-btn"
                  title="Kopyala"
                  onClick={async () => {
                    await navigator.clipboard.writeText(message.content);
                    setCopiedIndex(index);
                    setTimeout(() => setCopiedIndex(null), 1200);
                  }}
                  style={{
                    background: 'none',
                    border: 'none',
                    padding: 0,
                    marginLeft: 6,
                    cursor: 'pointer',
                    outline: 'none',
                    display: 'flex',
                    alignItems: 'center',
                    opacity: 0.7
                  }}
                >
                  <ContentCopyIcon sx={{ fontSize: 18, color: '#b0b8c4' }} />
                  {copiedIndex === index && (
                    <span style={{ fontSize: 12, color: '#4f8cff', marginLeft: 4 }}>Kopyalandı!</span>
                  )}
                </button>
              </Box>
            );
          })}
          {loading && (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}>
              <CircularProgress size={24} />
            </Box>
          )}
        </Box>

        {showCamera && (
          <Box
            sx={{
              position: 'fixed',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              bgcolor: 'rgba(0,0,0,0.9)',
              zIndex: 1000,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <video
              ref={videoRef}
              autoPlay
              playsInline
              style={{
                width: '100%',
                maxHeight: '80vh',
                objectFit: 'contain',
              }}
            />
            <canvas ref={canvasRef} style={{ display: 'none' }} />
            <Box sx={{ mt: 2, display: 'flex', gap: 2 }}>
              <Button
                variant="contained"
                color="primary"
                onClick={handleCapture}
                startIcon={<CameraAltIcon />}
              >
                Fotoğraf Çek
              </Button>
              <Button
                variant="outlined"
                color="error"
                onClick={stopCamera}
              >
                İptal
              </Button>
            </Box>
          </Box>
        )}

        <Box 
          component="form" 
          onSubmit={handleSubmit}
          className="input-footer"
        >
          <TextField
            fullWidth
            variant="outlined"
            placeholder="Mesajınızı yazın..."
            value={input}
            onChange={(e) => setInput(e.target.value)}
            disabled={loading}
            id="chat-input"
          />
          <Box sx={{ display: 'flex', gap: 1, ml: 1 }}>
            <input
              id="imageInput"
              type="file"
              accept="image/*"
              onChange={(e) => {
                const file = e.target.files[0];
                if (file) {
                  handleImageSearch(file);
                }
              }}
              style={{ display: 'none' }}
            />
            <button
              type="button"
              onClick={handleImageButtonClick}
              style={{
                background: 'linear-gradient(90deg, #4f8cff 0%, #6fc3ff 100%)',
                border: 'none',
                borderRadius: 12,
                padding: 0,
                width: 44,
                height: 44,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                cursor: 'pointer',
                outline: 'none',
                boxShadow: '0 2px 8px rgba(79,140,255,0.08)',
                transition: 'background 0.2s, box-shadow 0.2s',
                opacity: loading ? 0.5 : 1
              }}
              disabled={loading}
              title="Resimle Ara"
            >
              <ImageIcon sx={{ fontSize: 26, color: '#fff' }} />
            </button>
            <Menu
              anchorEl={anchorEl}
              open={Boolean(anchorEl)}
              onClose={handleMenuClose}
            >
              <MenuItem onClick={handleCameraSelect}>
                <CameraAltIcon sx={{ mr: 1 }} /> Kamera ile Çek
              </MenuItem>
              <MenuItem onClick={handleFileSelect}>
                <PhotoLibraryIcon sx={{ mr: 1 }} /> Galeriden Seç
              </MenuItem>
            </Menu>
            <button
              className="mic-btn"
              type="button"
              onClick={handleMicClick}
              style={{
                background: listening ? 'linear-gradient(90deg, #ff6f91 0%, #ffb86c 100%)' : 'linear-gradient(90deg, #4f8cff 0%, #6fc3ff 100%)',
                border: 'none',
                borderRadius: 12,
                padding: 0,
                width: 44,
                height: 44,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                cursor: 'pointer',
                outline: 'none',
                boxShadow: listening ? '0 0 0 4px #ffb86c33' : '0 2px 8px rgba(79,140,255,0.08)',
                transition: 'background 0.2s, box-shadow 0.2s',
                opacity: loading ? 0.5 : 1
              }}
              disabled={loading}
              title={listening ? 'Dinleniyor...' : 'Mikrofonla konuş'}
            >
              <MicIcon sx={{ fontSize: 26, color: '#fff' }} />
            </button>
            <Button 
              type="submit" 
              variant="contained" 
              endIcon={<SendIcon />}
              disabled={loading || !input.trim()}
            >
              Gönder
            </Button>
          </Box>
        </Box>
      </Paper>
    </Container>
  );
}

export default App; 